import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { AuthContext } from '../context/AuthContext';
import SEO from '../components/SEO';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    PaperAirplaneIcon, 
    SparklesIcon, 
    UserCircleIcon,
    CpuChipIcon,
    TrashIcon,
    ChartBarIcon,
    ClockIcon,
    BoltIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const AviationAIPage = () => {
    const { user, token } = useContext(AuthContext);
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const inputRef = useRef(null);
    
    // AI Metrics (simulated)
    const [metrics, setMetrics] = useState({
        cpuUsage: 45,
        tokensUsed: 0,
        responseTime: 0,
        model: 'ACD 1.0 Flash'
    });

    const scrollToBottom = () => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        setMessages([{
            role: 'assistant',
            content: `Welcome to ACD Aviation AI! 🚀\n\nI'm your expert aviation assistant powered by advanced AI. I can help you with:\n\n• Aviation concepts and aerodynamics\n• Aircraft systems and technology\n• Course recommendations\n• Space technology and aerospace engineering\n• Material science in aviation\n\nHow can I assist you today?`,
            timestamp: new Date()
        }]);
        setIsInitialized(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isInitialized && messages.length > 1) {
            scrollToBottom();
        }
    }, [messages, isInitialized]);

    // Simulate CPU usage fluctuation
    useEffect(() => {
        const interval = setInterval(() => {
            setMetrics(prev => ({
                ...prev,
                cpuUsage: Math.max(20, Math.min(85, prev.cpuUsage + (Math.random() - 0.5) * 10))
            }));
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        
        if (!inputMessage.trim()) return;

        if (!user || !token) {
            toast.error('Please sign in to use Aviation AI');
            navigate('/login');
            return;
        }

        const userMessage = {
            role: 'user',
            content: inputMessage,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setLoading(true);
        
        const startTime = Date.now();

        try {
            const conversationHistory = messages.map(msg => ({
                role: msg.role,
                content: msg.content
            }));

            const response = await axios.post(
                `${API_BASE_URL}/api/ai/chat`,
                {
                    message: inputMessage,
                    conversationHistory: conversationHistory
                },
                {
                    headers: {
                        'x-auth-token': token
                    }
                }
            );

            const assistantMessage = {
                role: 'assistant',
                content: response.data.response,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
            
            // Update metrics
            const responseTime = Date.now() - startTime;
            const estimatedTokens = Math.ceil((inputMessage.length + response.data.response.length) / 4);
            setMetrics(prev => ({
                ...prev,
                tokensUsed: prev.tokensUsed + estimatedTokens,
                responseTime: responseTime,
                cpuUsage: Math.min(95, prev.cpuUsage + Math.random() * 20)
            }));
        } catch (error) {
            console.error('AI Chat Error:', error);
            toast.error('Failed to get AI response. Please try again.');
            setMessages(prev => prev.filter(msg => msg !== userMessage));
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleClearChat = () => {
        setMessages([{
            role: 'assistant',
            content: `Chat cleared! How can I help you today?`,
            timestamp: new Date()
        }]);
        toast.success('Chat history cleared');
    };

    const formatMessage = (content) => {
        return content.split('\n').map((line, i) => (
            <React.Fragment key={i}>
                {line}
                {i < content.split('\n').length - 1 && <br />}
            </React.Fragment>
        ));
    };

    return (
        <>
            <SEO 
                title="Aviation AI Assistant | AircoreDynamics"
                description="Chat with our advanced AI assistant specialized in aviation, aerospace, and aerodynamics. Get instant expert answers to your questions."
                keywords="aviation AI, aerospace assistant, aerodynamics help, aviation chatbot"
                image="https://aircoredynamics.com/og-image.png"
                url="/aviation-ai"
                type="website"
            />
            
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col bg-white dark:bg-dark-card overflow-hidden"
                style={{ height: 'calc(100vh - 64px)' }}
            >
                {/* Chat Header with Stats */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-slate-800 dark:to-blue-900 px-4 md:px-6 py-3 border-b border-blue-500 dark:border-slate-700">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/30 dark:bg-white/20 p-2 rounded-xl">
                                <SparklesIcon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <h1 className="font-bold text-white text-lg">Aviation AI Assistant</h1>
                                <div className="flex items-center gap-4 text-xs text-white/90 dark:text-white/80">
                                    <span className="flex items-center gap-1">
                                        <CpuChipIcon className="h-3 w-3" />
                                        {metrics.model}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <BoltIcon className="h-3 w-3" />
                                        {metrics.tokensUsed} tokens
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            {/* CPU Usage Indicator */}
                            <div className="hidden md:flex items-center gap-2 bg-white/20 dark:bg-white/10 px-3 py-2 rounded-lg">
                                <ChartBarIcon className="h-4 w-4 text-white/90 dark:text-white/80" />
                                <div className="flex flex-col">
                                    <span className="text-xs text-white/70 dark:text-white/60">CPU</span>
                                    <span className="text-sm font-semibold text-white">{Math.round(metrics.cpuUsage)}%</span>
                                </div>
                                <div className="w-16 h-2 bg-white/30 dark:bg-white/20 rounded-full overflow-hidden">
                                    <motion.div 
                                        className="h-full bg-gradient-to-r from-blue-400 to-cyan-500"
                                        animate={{ width: `${metrics.cpuUsage}%` }}
                                        transition={{ duration: 0.5 }}
                                    />
                                </div>
                            </div>
                            
                            {metrics.responseTime > 0 && (
                                <div className="hidden lg:flex items-center gap-1.5 bg-white/20 dark:bg-white/10 px-3 py-2 rounded-lg">
                                    <ClockIcon className="h-4 w-4 text-white/90 dark:text-white/80" />
                                    <div className="flex flex-col">
                                        <span className="text-xs text-white/70 dark:text-white/60">Response</span>
                                        <span className="text-sm font-semibold text-white">{(metrics.responseTime / 1000).toFixed(2)}s</span>
                                    </div>
                                </div>
                            )}
                            
                            <button
                                onClick={handleClearChat}
                                className="text-white/90 hover:text-white hover:bg-white/30 dark:hover:bg-white/20 px-3 py-2 rounded-lg transition-all flex items-center gap-2"
                                title="Clear chat"
                            >
                                <TrashIcon className="h-5 w-5" />
                                <span className="hidden sm:inline">Clear</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Messages */}
                <div 
                    ref={messagesContainerRef}
                    className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950"
                >
                    <div className="max-w-4xl mx-auto w-full">
                        <AnimatePresence initial={false}>
                            {messages.map((message, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className={`flex gap-4 mb-6 ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                                >
                                    {/* Avatar */}
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                                        message.role === 'assistant' 
                                            ? 'bg-gradient-to-br from-blue-900 to-blue-700' 
                                            : 'bg-gradient-to-br from-slate-700 to-slate-600'
                                    }`}>
                                        {message.role === 'assistant' ? (
                                            <SparklesIcon className="h-6 w-6 text-white" />
                                        ) : (
                                            <UserCircleIcon className="h-6 w-6 text-white" />
                                        )}
                                    </div>

                                    {/* Message Content */}
                                    <div className={`flex-1 ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                                        <div className={`inline-block px-6 py-4 rounded-2xl max-w-full ${
                                            message.role === 'assistant'
                                                ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-md text-left'
                                                : 'bg-gradient-to-r from-slate-700 to-slate-600 text-white shadow-lg'
                                        }`}>
                                            <p className={`text-base leading-relaxed whitespace-pre-wrap ${
                                                message.role === 'assistant' 
                                                    ? 'text-gray-800 dark:text-gray-100' 
                                                    : 'text-white'
                                            }`}>
                                                {formatMessage(message.content)}
                                            </p>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 px-2">
                                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    {/* Loading Indicator */}
                    {loading && (
                        <div className="max-w-4xl mx-auto w-full">
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="flex gap-4 mb-6"
                            >
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-900 to-blue-700 flex items-center justify-center shadow-lg">
                                    <SparklesIcon className="h-6 w-6 text-white" />
                                </div>
                                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-6 py-4 rounded-2xl shadow-md">
                                    <div className="flex gap-2">
                                        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 md:px-6 py-4">
                    <div className="max-w-4xl mx-auto">
                        <form onSubmit={handleSendMessage} className="flex gap-3">
                            <input
                                ref={inputRef}
                                type="text"
                                value={inputMessage}
                                onChange={(e) => setInputMessage(e.target.value)}
                                placeholder={user ? "Message Aviation AI..." : "Sign in to start chatting..."}
                                disabled={loading}
                                className="flex-1 px-5 py-4 rounded-xl border-2 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 focus:border-blue-600 dark:focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all disabled:opacity-50 text-base"
                            />
                            <button
                                type="submit"
                                disabled={loading || !inputMessage.trim()}
                                className="px-6 py-4 bg-gradient-to-r from-blue-900 to-blue-700 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:hover:scale-100 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <PaperAirplaneIcon className="h-5 w-5" />
                                <span className="hidden md:inline">Send</span>
                            </button>
                        </form>
                        
                        <div className="mt-3 flex items-center justify-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                            {!user ? (
                                <p>
                                    <button onClick={() => navigate('/login')} className="text-blue-600 dark:text-blue-400 underline font-semibold hover:text-blue-700">Sign in</button> to use Aviation AI
                                </p>
                            ) : (
                                <p>
                                    AI can make mistakes. Check important info. <Link to="/terms" className="underline hover:text-gray-700 dark:hover:text-gray-300">Terms</Link> • <Link to="/privacy" className="underline hover:text-gray-700 dark:hover:text-gray-300">Privacy</Link>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </>
    );
};

export default AviationAIPage;
